<div class="clock-container">
  <!-- Time display header + optional AM/PM toggle -->
  <div class="clock-header-row">
    <div class="clock-time-display" role="group" aria-label="Selected time">
      <span
        class="clock-time-segment"
        [class.active]="view === 'hours'"
        [class.previewing]="view === 'hours' && hoverPreview !== null"
        role="button"
        tabindex="0"
        aria-label="Select hours"
        (click)="switchToHours()"
        (keydown.enter)="switchToHours()"
        (keydown.space)="switchToHours()"
      >
        {{ previewHours }}
      </span>
      <span class="clock-time-separator" aria-hidden="true">:</span>
      <span
        class="clock-time-segment"
        [class.active]="view === 'minutes'"
        [class.previewing]="view === 'minutes' && hoverPreview !== null"
        role="button"
        tabindex="0"
        aria-label="Select minutes"
        (click)="switchToMinutes()"
        (keydown.enter)="switchToMinutes()"
        (keydown.space)="switchToMinutes()"
      >
        {{ previewMinutes }}
      </span>
    </div>

    @if (use12Hours) {
      <div class="ampm-toggle" role="group" aria-label="AM or PM">
        <button
          type="button"
          class="ampm-btn"
          [class.ampm-btn--active]="amPm === 'AM'"
          aria-label="AM"
          (click)="amPmChange.emit('AM')"
        >
          AM
        </button>
        <button
          type="button"
          class="ampm-btn"
          [class.ampm-btn--active]="amPm === 'PM'"
          aria-label="PM"
          (click)="amPmChange.emit('PM')"
        >
          PM
        </button>
      </div>
    }
  </div>

  <!-- Clock Dial -->
  <svg
    #svgEl
    class="clock-dial"
    viewBox="0 0 256 256"
    width="256"
    height="256"
    role="group"
    tabindex="0"
    [attr.aria-label]="
      view === 'hours' ? 'Select hour, 24-hour format' : 'Select minute'
    "
    (click)="onDialClick($event)"
    (mousemove)="onDialMouseMove($event)"
    (mouseleave)="onDialMouseLeave()"
    (keydown)="onDialKeyDown($event)"
  >
    <!-- Clock face -->
    <circle cx="128" cy="128" r="113" class="clock-face" />

    <!-- Hover preview: group with CSS rotate() for smooth animation (behind numbers) -->
    @if (hoverPreview) {
      <g
        class="clock-hand-preview-group"
        [style.transform]="'rotate(' + hoverAngleDeg + 'deg)'"
      >
        <line
          class="clock-hand clock-hand--preview"
          x1="128"
          y1="128"
          x2="128"
          [attr.y2]="128 - hoverPreview.radius"
        />
        <circle
          class="clock-endpoint-preview"
          cx="128"
          [attr.cy]="128 - hoverPreview.radius"
          r="16"
        />
      </g>
    }

    <!-- Selected hand: only appears when there is a real value -->
    @if (hasValue) {
      <line
        class="clock-hand"
        x1="128"
        y1="128"
        [attr.x2]="handEndX"
        [attr.y2]="handEndY"
      />
    }

    <!-- Clock center -->
    <circle cx="128" cy="128" r="4" class="clock-center-dot" />

    <!--
      Numbers: two separate @if blocks (hours vs else) so Angular
      destroys and recreates the <g> on each view change, triggering CSS animation.
    -->
    @if (view === 'hours') {
      <g class="dial-view-animated">
        @for (num of clockNumbers; track num.value) {
          <g
            class="clock-number"
            [class.clock-number--selected]="num.isSelected"
            [class.clock-number--inner]="num.isInner"
            [class.clock-number--hovered]="
              hoverPreview?.value === num.value && !num.isSelected
            "
            role="button"
            [attr.aria-label]="num.label"
            [attr.aria-pressed]="num.isSelected"
          >
            <circle
              [attr.cx]="num.x"
              [attr.cy]="num.y"
              [attr.r]="num.isInner ? 14 : 16"
              class="clock-number-bg"
            />
            <text
              [attr.x]="num.x"
              [attr.y]="num.y"
              class="clock-number-text"
              dominant-baseline="central"
              text-anchor="middle"
            >
              {{ num.label }}
            </text>
          </g>
        }
      </g>
    } @else {
      <g class="dial-view-animated">
        <!-- Minute tick marks (only in minutes view) -->
        @for (tick of minuteTicks; track $index) {
          <line
            [attr.x1]="tick.x1"
            [attr.y1]="tick.y1"
            [attr.x2]="tick.x2"
            [attr.y2]="tick.y2"
            class="clock-tick"
            [class.clock-tick-major]="tick.isMajor"
          />
        }
        @for (num of clockNumbers; track num.value) {
          <g
            class="clock-number"
            [class.clock-number--selected]="num.isSelected"
            [class.clock-number--inner]="num.isInner"
            [class.clock-number--hovered]="
              hoverPreview?.value === num.value && !num.isSelected
            "
            role="button"
            [attr.aria-label]="num.label"
            [attr.aria-pressed]="num.isSelected"
          >
            <circle
              [attr.cx]="num.x"
              [attr.cy]="num.y"
              [attr.r]="num.isInner ? 14 : 16"
              class="clock-number-bg"
            />
            <text
              [attr.x]="num.x"
              [attr.y]="num.y"
              class="clock-number-text"
              dominant-baseline="central"
              text-anchor="middle"
            >
              {{ num.label }}
            </text>
          </g>
        }
      </g>
    }
  </svg>
</div>
